/**
 * Generative-UI spike — a minimal multi-agent-style chat where the agent's reply
 * can be TEXT or a JSON UI spec rendered inline (the "widget in the message
 * stream" pattern). Self-contained (mock agent, no backend), isolated at
 * /spike/generative-ui. Proves the approach in our real app (CRA + Tailwind).
 *
 * Real version: the backend agent emits the UISpec (constrained to the catalog);
 * here a mock returns a canned spec so the loop is demonstrable without a backend.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Bot, User, Send } from 'lucide-react';
import { JsonRenderer } from './JsonRenderer';
import { UISpec, SAMPLE_PROJECT_STATUS } from './uiSpec';

type Message =
  | { id: number; from: 'user' | 'agent'; kind: 'text'; text: string }
  | { id: number; from: 'agent'; kind: 'ui'; spec: UISpec; caption?: string };

let nextId = 1;

/** Mock agent: a status/plan prompt yields a UI widget; otherwise text. */
function mockAgentReply(prompt: string): Message {
  const p = prompt.toLowerCase();
  if (p.includes('status') || p.includes('plan') || p.includes('progress') || p.includes('board')) {
    return { id: nextId++, from: 'agent', kind: 'ui', spec: SAMPLE_PROJECT_STATUS, caption: "Here's where the project stands:" };
  }
  return {
    id: nextId++,
    from: 'agent',
    kind: 'text',
    text: "Try asking for the project status (e.g. “show me the status”) — I’ll render a live widget inline instead of sending you to another page.",
  };
}

export default function GenerativeChatSpike() {
  const [messages, setMessages] = useState<Message[]>([
    { id: nextId++, from: 'agent', kind: 'text', text: 'Hi — I’m Atlas, your project planner. Ask me for the project status.' },
  ]);
  const [input, setInput] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setMessages((m) => [...m, { id: nextId++, from: 'user', kind: 'text', text }]);
    setInput('');
    setTimeout(() => setMessages((m) => [...m, mockAgentReply(text)]), 350);
  };

  return (
    <div className="mx-auto flex h-screen max-w-2xl flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Bot className="h-5 w-5 text-slate-900" /> Generative-UI spike — agent chat
        </div>
        <p className="text-xs text-slate-500">Widgets render inline from a JSON spec (json-render pattern). Isolated demo.</p>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex gap-2 ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.from === 'agent' && <Bot className="mt-1 h-6 w-6 shrink-0 rounded-full bg-slate-900 p-1 text-white" />}
            <div className={`max-w-[85%] ${msg.from === 'user' ? 'order-1' : ''}`}>
              {msg.kind === 'text' ? (
                <div
                  className={
                    msg.from === 'user'
                      ? 'rounded-2xl rounded-br-sm bg-slate-900 px-3 py-2 text-sm text-white'
                      : 'rounded-2xl rounded-bl-sm bg-white px-3 py-2 text-sm text-slate-700 shadow-sm'
                  }
                >
                  {msg.text}
                </div>
              ) : (
                <div className="space-y-2">
                  {msg.caption && <div className="rounded-2xl rounded-bl-sm bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">{msg.caption}</div>}
                  {/* The generative widget, rendered inline in the stream */}
                  <JsonRenderer spec={msg.spec} onAction={(a) => console.info(`[spike] action: ${a} (would open the board in the real app)`)} />
                </div>
              )}
            </div>
            {msg.from === 'user' && <User className="order-2 mt-1 h-6 w-6 shrink-0 rounded-full bg-slate-200 p-1 text-slate-600" />}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="border-t border-slate-200 bg-white p-3">
        <div className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Ask for the project status…"
            className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm focus:border-slate-900 focus:outline-none"
          />
          <button onClick={send} className="rounded-full bg-slate-900 p-2 text-white hover:bg-slate-700" aria-label="Send">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
